import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Header } from './Header';
import { Footer } from './Footer';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { messeData } from '../mock';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';

const EVENT_DATE = new Date('2026-09-19');
const ONE_MONTH_BEFORE = new Date(EVENT_DATE);
ONE_MONTH_BEFORE.setMonth(ONE_MONTH_BEFORE.getMonth() - 1);

const API_URL = process.env.REACT_APP_API_URL || 'https://nextgenbusinesscup.com/api';
const FRONTEND_ORIGIN = window.location.origin;

const FieldError = ({ message, id }) =>
  message ? (
    <p id={id} className="mt-1.5 flex items-center gap-1.5 text-sm text-amber-700" role="alert">
      <svg className="h-4 w-4 shrink-0 text-amber-500" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
      </svg>
      {message}
    </p>
  ) : null;

export const Kaufen = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [ticketInfo, setTicketInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isCreatingCheckout, setIsCreatingCheckout] = useState(false);
  const [error, setError] = useState('');

  const [discountCode, setDiscountCode] = useState('');
  const [invoiceName, setInvoiceName] = useState('');
  const [invoiceStreet, setInvoiceStreet] = useState('');
  const [invoiceZip, setInvoiceZip] = useState('');
  const [invoiceCity, setInvoiceCity] = useState('');
  const [invoiceCountry, setInvoiceCountry] = useState('DE');

  const [agbAccepted, setAgbAccepted] = useState(false);
  const [widerrufAccepted, setWiderrufAccepted] = useState(false);
  const [datenschutzAccepted, setDatenschutzAccepted] = useState(false);

  const [fieldErrors, setFieldErrors] = useState({});

  const paymentStatus = searchParams.get('payment');

  useEffect(() => {
    if (user?.first_name && user?.last_name) {
      setInvoiceName(`${user.first_name} ${user.last_name}`.trim());
    } else if (user?.company_name) {
      setInvoiceName(user.company_name);
    }
  }, [user]);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API_URL}/tickets/info`);
        setTicketInfo(res.data);
      } catch (_) {
        setTicketInfo(null);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  useEffect(() => {
    if (paymentStatus === 'success') {
      navigate('/dashboard?payment=success', { replace: true });
      return;
    }
    if (paymentStatus === 'cancel') {
      setError('Zahlung abgebrochen. Du kannst es erneut versuchen.');
    }
  }, [paymentStatus, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const errors = {};
    if (!invoiceName.trim()) errors.invoice_name = 'Bitte Name oder Firma angeben.';
    if (!invoiceStreet.trim()) errors.invoice_street = 'Bitte Straße und Hausnummer angeben.';
    if (!invoiceZip.trim()) errors.invoice_zip = 'Bitte PLZ angeben.';
    if (!invoiceCity.trim()) errors.invoice_city = 'Bitte Ort angeben.';
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setError('Bitte alle Pflichtfelder der Rechnungsadresse ausfüllen.');
      return;
    }
    if (!agbAccepted || !widerrufAccepted || !datenschutzAccepted) {
      setError('Bitte akzeptiere die AGB, das Widerrufsrecht und die Datenschutzhinweise.');
      return;
    }
    setIsCreatingCheckout(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/tickets/checkout-session`,
        {
          discount_code: discountCode.trim() || undefined,
          success_url: `${FRONTEND_ORIGIN}/dashboard`,
          cancel_url: `${FRONTEND_ORIGIN}/kaufen`,
          invoice_name: invoiceName.trim(),
          invoice_street: invoiceStreet.trim(),
          invoice_zip: invoiceZip.trim(),
          invoice_city: invoiceCity.trim(),
          invoice_country: invoiceCountry.trim() || 'DE',
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
        return;
      }
      setError('Keine Zahlungs-URL erhalten.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Erstellen der Zahlung.');
    } finally {
      setIsCreatingCheckout(false);
    }
  };

  if (paymentStatus === 'success') return null;

  return (
    <div className="min-h-screen flex flex-col bg-ngbc-beige">
      <Header />
      <main className="flex-grow max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16 mt-20">
        <h1 className="text-3xl font-bold text-ngbc-dark mb-2">{t('portal.ticketKaufenTitle')}</h1>
        <p className="text-ngbc-dark-text mb-8">{messeData.datum} · {messeData.ort}</p>

        {loading ? (
          <p className="text-ngbc-muted">{t('portal.preiseWerdenGeladen')}</p>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {ticketInfo && (
              <div className="bg-ngbc-primary/10 rounded-xl p-4 border border-ngbc-primary/20">
                <p className="text-ngbc-dark">{t('portal.aktuellerPreisLabel')} <strong>{ticketInfo.price_eur != null ? `${Number(ticketInfo.price_eur).toFixed(2)} €` : '–'}</strong></p>
                <p className="text-ngbc-primary font-semibold mt-1">{t('portal.studiPreisLabel')} {ticketInfo.student_price_eur != null ? `${Number(ticketInfo.student_price_eur).toFixed(2)} €` : '3,00 €'}</p>
                {ticketInfo.tickets_remaining != null && new Date() >= ONE_MONTH_BEFORE && (
                  <p className="text-ngbc-muted text-sm mt-2">{t('portal.ticketsVerfuegbarLabel', { n: ticketInfo.tickets_remaining })}</p>
                )}
              </div>
            )}

            <div className="bg-ngbc-beige rounded-xl shadow-md p-6 border border-ngbc-secondary">
              <h2 className="text-lg font-semibold text-ngbc-dark mb-4">{t('portal.rechnungsadresse')}</h2>
              <p className="text-sm text-ngbc-dark-text mb-4">{t('portal.rechnungsadresseInfo')}</p>
              <div className="grid gap-4">
                <div>
                  <Label htmlFor="invoice_name">{t('portal.nameFirma')}</Label>
                  <Input
                    id="invoice_name"
                    value={invoiceName}
                    onChange={(e) => { setInvoiceName(e.target.value); setFieldErrors((prev) => ({ ...prev, invoice_name: '' })); }}
                    placeholder="Max Mustermann"
                    className={`mt-1 ${fieldErrors.invoice_name ? 'border-amber-500 focus-visible:ring-amber-500' : ''}`}
                    aria-invalid={!!fieldErrors.invoice_name}
                    aria-describedby={fieldErrors.invoice_name ? 'invoice_name_error' : undefined}
                  />
                  <FieldError message={fieldErrors.invoice_name} id="invoice_name_error" />
                </div>
                <div>
                  <Label htmlFor="invoice_street">{t('portal.strasseHausnummer')}</Label>
                  <Input
                    id="invoice_street"
                    value={invoiceStreet}
                    onChange={(e) => { setInvoiceStreet(e.target.value); setFieldErrors((prev) => ({ ...prev, invoice_street: '' })); }}
                    placeholder="Musterstraße 1"
                    className={`mt-1 ${fieldErrors.invoice_street ? 'border-amber-500 focus-visible:ring-amber-500' : ''}`}
                    aria-invalid={!!fieldErrors.invoice_street}
                    aria-describedby={fieldErrors.invoice_street ? 'invoice_street_error' : undefined}
                  />
                  <FieldError message={fieldErrors.invoice_street} id="invoice_street_error" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="invoice_zip">{t('portal.plz')}</Label>
                    <Input
                      id="invoice_zip"
                      value={invoiceZip}
                      onChange={(e) => { setInvoiceZip(e.target.value); setFieldErrors((prev) => ({ ...prev, invoice_zip: '' })); }}
                      placeholder="50667"
                      className={`mt-1 ${fieldErrors.invoice_zip ? 'border-amber-500 focus-visible:ring-amber-500' : ''}`}
                      aria-invalid={!!fieldErrors.invoice_zip}
                      aria-describedby={fieldErrors.invoice_zip ? 'invoice_zip_error' : undefined}
                    />
                    <FieldError message={fieldErrors.invoice_zip} id="invoice_zip_error" />
                  </div>
                  <div>
                    <Label htmlFor="invoice_city">{t('portal.ort')}</Label>
                    <Input
                      id="invoice_city"
                      value={invoiceCity}
                      onChange={(e) => { setInvoiceCity(e.target.value); setFieldErrors((prev) => ({ ...prev, invoice_city: '' })); }}
                      placeholder="Köln"
                      className={`mt-1 ${fieldErrors.invoice_city ? 'border-amber-500 focus-visible:ring-amber-500' : ''}`}
                      aria-invalid={!!fieldErrors.invoice_city}
                      aria-describedby={fieldErrors.invoice_city ? 'invoice_city_error' : undefined}
                    />
                    <FieldError message={fieldErrors.invoice_city} id="invoice_city_error" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="invoice_country">{t('portal.land')}</Label>
                  <Input id="invoice_country" value={invoiceCountry} onChange={(e) => setInvoiceCountry(e.target.value)} placeholder="DE" className="mt-1" />
                </div>
              </div>
            </div>

            <div className="bg-ngbc-beige rounded-xl shadow-md p-6 border border-ngbc-secondary">
              <Label htmlFor="discount_code">{t('portal.hochschulCodeOptional')}</Label>
              <Input id="discount_code" value={discountCode} onChange={(e) => setDiscountCode(e.target.value.toUpperCase())} placeholder="z.B. HS-MUENCHEN-2026" className="mt-1" />
            </div>

            <div className="bg-ngbc-beige rounded-xl shadow-md p-6 border border-ngbc-secondary space-y-4">
              <h2 className="text-lg font-semibold text-ngbc-dark">{t('portal.rechtlicheHinweise')}</h2>
              <div className="flex items-start gap-3">
                <Checkbox id="agb" checked={agbAccepted} onCheckedChange={(v) => setAgbAccepted(!!v)} />
                <label htmlFor="agb" className="text-sm text-ngbc-dark cursor-pointer">
                  {t('portal.agbAkzeptiert').split('*')[0]}<a href="/agb" target="_blank" rel="noopener noreferrer" className="text-ngbc-primary underline">{t('portal.agbLink')}</a>{t('portal.agbAkzeptiert').includes('*') ? ' *' : ''}
                </label>
              </div>
              <div className="flex items-start gap-3">
                <Checkbox id="widerruf" checked={widerrufAccepted} onCheckedChange={(v) => setWiderrufAccepted(!!v)} />
                <label htmlFor="widerruf" className="text-sm text-ngbc-dark cursor-pointer">
                  {t('portal.widerrufsrechtAkzeptiert').split('*')[0]}<a href="/datenschutz" target="_blank" rel="noopener noreferrer" className="text-ngbc-primary underline">{t('portal.widerrufsrecht')}</a>{t('portal.widerrufsrechtAkzeptiert').includes('*') ? ' *' : ''}
                </label>
              </div>
              <div className="flex items-start gap-3">
                <Checkbox id="datenschutz" checked={datenschutzAccepted} onCheckedChange={(v) => setDatenschutzAccepted(!!v)} />
                <label htmlFor="datenschutz" className="text-sm text-ngbc-dark cursor-pointer">
                  {t('portal.datenschutzAkzeptiert').split('*')[0]}<a href="/datenschutz" target="_blank" rel="noopener noreferrer" className="text-ngbc-primary underline">{t('portal.datenschutzerklaerungLink')}</a>{t('portal.datenschutzAkzeptiert').includes('*') ? ' *' : ''}
                </label>
              </div>
            </div>

            {error && <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700 text-sm">{error}</div>}

            <Button type="submit" disabled={!ticketInfo || isCreatingCheckout || (ticketInfo?.tickets_remaining === 0)} className="w-full py-6 text-lg bg-ngbc-primary hover:bg-ngbc-accent">
              {isCreatingCheckout ? t('portal.weiterleitungZahlung') : ticketInfo?.tickets_remaining === 0 ? t('portal.ausverkauftLabel') : t('portal.jetztKaufen')}
            </Button>

            <p className="text-xs text-ngbc-muted text-center">
              {t('portal.kaufErfolgInfo')}
            </p>
          </form>
        )}
      </main>
      <Footer />
    </div>
  );
};
